import { motion, useMotionValue, useTransform } from 'framer-motion';
import type { Variants, PanInfo } from 'framer-motion';
import { Quote, HelpCircle, Zap, Lightbulb, Target } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../lib/utils';
import { useEffect, useRef, useState } from 'react';

export type CardType = 'quote' | 'question' | 'gif' | 'fact' | 'challenge';

interface CardProps {
  id: string;
  type: CardType;
  content: {
    text: string;
    author?: string;
    imageUrl?: string;
    options?: string[];
  };
  isActive?: boolean;
  onSwipe: (direction: 'left' | 'right') => void;
}

const cardTypeIcons: Record<CardType, LucideIcon> = {
  quote: Quote,
  question: HelpCircle,
  gif: Zap,
  fact: Lightbulb,
  challenge: Target,
};

const cardTypeColors: Record<CardType, string> = {
  quote: 'bg-slate-800 text-blue-300 border border-blue-500/20',
  question: 'bg-slate-800 text-purple-300 border border-purple-500/20',
  gif: 'bg-slate-800 text-pink-300 border border-pink-500/20',
  fact: 'bg-slate-800 text-amber-300 border border-amber-500/20',
  challenge: 'bg-slate-800 text-green-300 border border-green-500/20',
};

const cardVariants: Variants = {
  hidden: { scale: 0.9, opacity: 0, y: 20 },
  visible: { 
    scale: 1, 
    opacity: 1, 
    y: 0,
    transition: { 
      type: 'spring',
      stiffness: 300,
      damping: 30,
    },
  },
  exit: (direction: number) => ({
    x: direction > 0 ? 500 : -500,
    opacity: 0,
    scale: 0.8,
    transition: { duration: 0.3 },
  }),
};

export default function Card({ id, type, content, isActive = true, onSwipe }: CardProps) {
  const Icon = cardTypeIcons[type];
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardWidth, setCardWidth] = useState(0);
  
  // Generate a random initial rotation between -15 and +15 degrees
  const [initialRotation] = useState(() => (Math.random() - 0.5) * 10);
  
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-100, 0, 100], [-7 + initialRotation, initialRotation, 7 + initialRotation]);
  
  // Calculate threshold to swipe off as 200% of card width
  const threshold = cardWidth * 0.4;
  
  // Calculate opacity for skip/add indicators based on drag distance
  const leftOpacity = useTransform(
    x,
    [-threshold, -threshold * 0.5, 0],
    [1, 0.5, 0]
  );
  
  const rightOpacity = useTransform(
    x,
    [0, threshold * 0.5, threshold],
    [0, 0.5, 1]
  );

  useEffect(() => {
    if (cardRef.current) {
      setCardWidth(cardRef.current.offsetWidth);
    }
  }, []);
  
  const handleDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (Math.abs(info.offset.x) > threshold) {
      onSwipe(info.offset.x > 0 ? 'right' : 'left');
    }
  };

  return (
    <motion.div
      ref={cardRef}
      key={id}
      className={cn(
        'absolute w-[90%] max-w-md h-[60vh] max-h-[600px] rounded-2xl p-6 shadow-lg flex flex-col',
        cardTypeColors[type],
        !isActive ? 'pointer-events-none' : 'cursor-grab active:cursor-grabbing',
        'touch-none' // Prevent text selection during drag
      )}
        drag={isActive ? 'x' : false}
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.9}
        dragMomentum={false}
        dragTransition={{ bounceStiffness: 600, bounceDamping: 20 }}
        onDragEnd={handleDragEnd}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        custom={1}
        style={{
          x,
          rotate,
          touchAction: 'none',
          zIndex: isActive ? 10 : 1,
        }}
      >
        <div className="flex items-center gap-2 mb-4">
          <Icon className="w-6 h-6" />
          <span className="font-medium capitalize">{type}</span>
        </div>
        
        <div className="flex-1 flex flex-col justify-center items-center text-center overflow-hidden">
          {content.imageUrl ? (
            <img 
              src={content.imageUrl} 
              alt="Card content" 
              className="max-h-[40vh] max-w-full rounded-lg object-cover mb-4"
              draggable={false}
            />
          ) : null}
          
          <p className="text-lg mb-4">{content.text}</p>
          
          {content.author && (
            <p className="text-sm opacity-70 mt-2">— {content.author}</p>
          )}
          
          {content.options?.length ? (
            <div className="mt-4 w-full space-y-2">
              {content.options.map((option, i) => (
                <button
                  key={i}
                  className="w-full p-3 bg-white/50 rounded-lg text-sm font-medium hover:bg-white/70 transition-colors"
                  onClick={() => console.log('Selected:', option)}
                >
                  {option}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        
        {/* Skip/Add Overlays */}
        <motion.div 
          className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 rounded-2xl bg-red-500/20"
          style={{ opacity: leftOpacity }}
        >
          <span className="text-4xl font-bold tracking-wider text-red-600">SKIP</span>
        </motion.div>
        
        <motion.div 
          className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 rounded-2xl bg-green-500/20"
          style={{ opacity: rightOpacity }}
        >
          <span className="text-4xl font-bold tracking-wider text-green-600">ADD</span>
        </motion.div>
      </motion.div>
  );
}
